import React from 'react'
import {
  Dialog,
  DialogContent,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Divider,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Button
} from '@mui/material'
import moment from 'moment'

const BillDetailsDialog = ({ open, onClose, billData, loading, error }) => {
  console.log('=== BillDetailsDialog Component Rendered ===')
  console.log('Props:', { open, loading, error })
  console.log('billData:', JSON.stringify(billData, null, 2))
  console.log('billData type:', typeof billData)
  console.log('billData is null?', billData === null)
  console.log('billData is undefined?', billData === undefined)
  console.log('===========================================')

  if (!billData && !loading) return null

  const formatCurrency = (amount) => {
    return amount ? `${parseFloat(amount).toFixed(2)}` : '0.00'
  }

  const formatDate = (date) => {
    return date ? moment(date).format('DD-MM-YYYY HH:mm:ss') : 'N/A'
  }

  const formatPercentage = (value) => {
    return value ? parseFloat(value).toFixed(2) : '0.00'
  }

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          margin: '20px',
          maxWidth: '400px',
          width: '100%'
        }
      }}
    >
      <DialogContent sx={{ p: 0, backgroundColor: 'transparent' }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            p={3}
            bgcolor="white"
            borderRadius={2}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : billData?.data ? (
          <Paper
            elevation={3}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px 12px 0 0',
              overflow: 'visible',
              position: 'relative',
              // Zigzag effect using SVG pattern
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -10,
                left: 0,
                right: 0,
                height: '10px',
                background: `url("data:image/svg+xml,%3Csvg width='20' height='10' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10 L10 0 L20 10' fill='%23ffffff'/%3E%3C/svg%3E") repeat-x`,
                zIndex: 1
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: 0,
                right: 0,
                height: '10px',
                background: `url("data:image/svg+xml,%3Csvg width='20' height='10' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0 L10 10 L20 0' fill='%23ffffff'/%3E%3C/svg%3E") repeat-x`,
                zIndex: 1
              }
            }}
          >
            {/* Scrollable content */}
            <Box
              sx={{
                maxHeight: 'calc(90vh - 100px)',
                overflowY: 'auto',
                p: '14px 12px 8px 12px',
                pt: '20px'
              }}
            >
              {/* Bill Header */}
              <Box textAlign="center" mb={1}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ fontSize: '1.5rem' }}
                >
                  Bill
                </Typography>
              </Box>

              <Box sx={{ mb: 1 }}>
                {/* Date and Bill No */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Date : {formatDate(billData.data.date_time)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Bill No : {billData.data.bill_no || 'N/A'}
                  </Typography>
                </Box>

                {/* Waiter and Guest Type */}
                {(billData.data.waiter?.name || billData.data.guest_type) && (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    {billData.data.waiter?.name && (
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Waiter : {billData.data.waiter.name}
                      </Typography>
                    )}
                    {billData.data.guest_type && (
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Guest : {billData.data.guest_type}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Floor and Table */}
                {billData.data.table?.name && (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    {billData.data.floor?.name && (
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Floor : {billData.data.floor.name}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Table : {billData.data.table.name}
                    </Typography>
                  </Box>
                )}

                {/* User */}
                {billData.data.user?.first_name && (
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    User : {billData.data.user.first_name}
                  </Typography>
                )}
              </Box>

              {/* Customer Details */}
              {billData.data.customer &&
                (billData.data.customer.name ||
                  billData.data.customer.mobile ||
                  billData.data.customer.gstn) && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 0.75, fontSize: '0.9rem' }}
                    >
                      Customer Details
                    </Typography>

                    {billData.data.customer.name && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        sx={{ mb: 0.25 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.875rem' }}
                        >
                          Name :
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            textAlign: 'right',
                            maxWidth: '60%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {billData.data.customer.name}
                        </Typography>
                      </Box>
                    )}

                    {billData.data.customer.mobile && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        sx={{ mb: 0.25 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.875rem' }}
                        >
                          Mobile :
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.875rem' }}
                        >
                          {billData.data.customer.mobile}
                        </Typography>
                      </Box>
                    )}

                    {billData.data.customer.gstn && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        sx={{ mb: 0.25 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.875rem' }}
                        >
                          GSTIN :
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            textAlign: 'right',
                            maxWidth: '60%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {billData.data.customer.gstn}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

              <Divider sx={{ my: 1 }} />

              {/* Items Table - Compact Version */}
              <Box sx={{ mb: 1 }}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    py: 0.5,
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{ flex: '0 0 30px', fontSize: '0.75rem' }}
                  >
                    Sl
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{ flex: '1 1 auto', fontSize: '0.75rem' }}
                  >
                    Item
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="right"
                    sx={{ flex: '0 0 50px', fontSize: '0.75rem' }}
                  >
                    Qty
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="right"
                    sx={{ flex: '0 0 60px', fontSize: '0.75rem' }}
                  >
                    Price
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="right"
                    sx={{ flex: '0 0 60px', fontSize: '0.75rem' }}
                  >
                    Total
                  </Typography>
                </Box>

                {/* Table Rows */}
                {billData.data.bill_items &&
                billData.data.bill_items.length > 0 ? (
                  billData.data.bill_items.map((item, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{ py: 0.5, alignItems: 'flex-start' }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ flex: '0 0 30px', fontSize: '0.875rem' }}
                      >
                        {index + 1}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ flex: '1 1 auto', fontSize: '0.875rem' }}
                      >
                        {item.item_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        align="right"
                        sx={{ flex: '0 0 50px', fontSize: '0.875rem' }}
                      >
                        {item.qty ? item.qty.toFixed(0) : '0'}
                      </Typography>
                      <Typography
                        variant="body2"
                        align="right"
                        sx={{ flex: '0 0 60px', fontSize: '0.875rem' }}
                      >
                        {item.unit_price ? item.unit_price.toFixed(2) : '0.00'}
                      </Typography>
                      <Typography
                        variant="body2"
                        align="right"
                        sx={{ flex: '0 0 60px', fontSize: '0.875rem' }}
                      >
                        {item.unit_price && item.qty
                          ? (item.unit_price * item.qty).toFixed(2)
                          : '0.00'}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box textAlign="center" py={2}>
                    <Typography variant="body2" color="textSecondary">
                      No items found
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Summary Section */}
              <Box sx={{ mb: 1 }}>
                <Box display="flex" justifyContent="space-between" mb={0.25}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Subtotal :
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {formatCurrency(billData.data.sub_total)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.25}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Taxable Amt :
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {formatCurrency(billData.data.taxableAmt)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Tax Breakdown Table - Compact */}
              <Box sx={{ mb: 1 }}>
                {/* Tax Table Header */}
                <Box
                  display="flex"
                  sx={{
                    py: 0.5,
                    backgroundColor: '#f5f5f5'
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="center"
                    sx={{ flex: 1, fontSize: '0.75rem' }}
                  >
                    Taxable AMT
                  </Typography>

                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="center"
                    sx={{ flex: 1, fontSize: '0.75rem' }}
                  >
                    CGST
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="center"
                    sx={{ flex: 1, fontSize: '0.75rem' }}
                  >
                    SGST
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    align="center"
                    sx={{ flex: 1, fontSize: '0.75rem' }}
                  >
                    Tot GST
                  </Typography>
                </Box>

                {/* Tax Table Row */}
                <Box
                  display="flex"
                  sx={{
                    py: 0.5,
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ flex: 1, fontSize: '0.875rem' }}
                  >
                    {formatCurrency(
                      billData.data.taxableAmt || billData.data.sub_total
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ flex: 1, fontSize: '0.875rem' }}
                  >
                    {formatCurrency(billData.data.cgst_total)}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ flex: 1, fontSize: '0.875rem' }}
                  >
                    {formatCurrency(billData.data.sgst_total)}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ flex: 1, fontSize: '0.875rem' }}
                  >
                    {formatCurrency(billData.data.gst_total)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Final Totals */}
              <Box sx={{ mb: 1 }}>
                <Box display="flex" justifyContent="space-between" mb={0.25}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Total GST :
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {formatCurrency(billData.data.gst_total)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={0.25}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Round Off :
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {formatCurrency(billData.data.roundOffAmt)}
                  </Typography>
                </Box>

                {/* Service Charge - Show if enabled and amount > 0 */}
                {billData.data.servicecharge_status &&
                  billData.data.servicecharge_amount > 0 && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      mb={0.25}
                    >
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Service Charge :{' '}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          (@ {formatPercentage(billData.data.servicecharge)}%)
                        </Typography>
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(billData.data.servicecharge_amount)}
                      </Typography>
                    </Box>
                  )}

                {/* Grand Total */}
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Grand Total :
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {formatCurrency(billData.data.grand_total)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Payment */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  Payment :
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {billData.data.payment_mode || 'To Be Paid'}
                </Typography>
              </Box>
            </Box>

            {/* Fixed Close Button */}
            <Divider sx={{ borderColor: 'grey.400' }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: '8px 12px'
              }}
            >
              <Button
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  color: 'primary.main',
                  fontSize: '0.875rem'
                }}
              >
                Close
              </Button>
            </Box>
          </Paper>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export default BillDetailsDialog
